import { Container, Graphics, Rectangle, Sprite, Text, type Texture } from "pixi.js";
import { placementFor } from "../art/itemArt.js";
import type { ItemArt } from "../art/itemArt.js";
import type { LoadedClip, LoadedLayer } from "../art/loader.js";
import { FACINGS, GESTURE_DEFS, MIRRORED_FACING, type Facing, type Gesture } from "@clubap/shared";

/**
 * Altura del avatar en unidades de mundo. Los sprites se escalan a esta altura,
 * así que el arte puede venir en cualquier resolución sin tocar código.
 */
export const AVATAR_HEIGHT = 52;

/** Fotogramas por segundo del ciclo de caminata pre-renderizado. */
const WALK_FPS = 10;

const BODY_RY = AVATAR_HEIGHT / 2;
const BODY_RX = 21;

export interface AvatarView {
  root: Container;
  /** Se mueve con el rebote procedural. El nombre y el globo no. */
  body: Container;
  /**
   * El nombre y el globo, en un contenedor SUELTO del avatar.
   *
   * No cuelga de `root` a propósito, y no es un detalle de organización: `root` se
   * ordena por la posición de los pies, así que cualquier mueble con el borde inferior
   * más abajo se dibujaba encima del globo. El mesón de PCs de TAWS (zIndex 545) tapaba
   * lo que decía cualquiera parado más arriba, que es casi toda la sala.
   *
   * Quien lo monta le da su propia capa por encima del mobiliario y lo mueve cada
   * fotograma. Ver `RoomStage.nameplates`.
   */
  nameplate: Container;
  label: Text;
  bubble: Container;
  /**
   * True si el arte trae ciclo de caminata propio. Cuando lo es, el rebote
   * procedural debe apagarse: el rig ya anima y sumarlos se ve mal.
   */
  animated: boolean;
  /** Una sola llamada por fotograma: dirección, pose y avance del reloj. */
  update: (facing: Facing, walking: boolean, deltaSeconds: number) => void;
  /**
   * Enciende y apaga las capas de ítems según lo que el jugador lleve puesto.
   * Los sprites se pre-construyen, así que equipar sólo cambia visibilidad — no
   * hay que reconstruir nada en caliente.
   */
  setEquipped: (itemIds: readonly string[]) => void;
  /**
   * El glifo que flota sobre la cabeza mientras dura el gesto.
   *
   * El avatar dibuja la ETIQUETA y `RoomStage` anima el cuerpo. La división no es
   * arbitraria: el `nameplate` es del avatar, y `body.y`/`body.rotation` ya los
   * escribe el bucle de render. Partirlo al revés dejaría dos dueños del mismo
   * contenedor.
   */
  setGesture: (gesture: Gesture) => void;
}

/** Una dirección resuelta: qué fotogramas usar y si van espejados. */
interface ResolvedFrames {
  textures: Texture[];
  mirrored: boolean;
  /**
   * De qué dirección salió el dibujo, que no es hacia dónde mira el jugador.
   *
   * Existe para que la COLOCACIÓN del ítem se lea de la misma dirección que la
   * textura. Resolverlas por separado —cada una con su propia regla de espejo—
   * las deja discrepar en cuanto un ítem declare dibujos para las ocho y
   * desplazamientos para cinco: el sombrero se iría al otro lado de la cabeza
   * sólo en las direcciones espejadas, que es un bug de los que se ven tarde.
   */
  source: Facing;
}

type ResolvedClip = Map<Facing, ResolvedFrames>;

interface SpriteLayer {
  sprite: Sprite;
  idle: ResolvedClip;
  walk: ResolvedClip | null;
  scale: number;
  /** `null` para las capas del cuerpo, que siempre se ven. */
  itemId: string | null;
  /** Sólo los ítems se colocan; el cuerpo es la referencia. */
  placement?: ItemArt["placement"];
}

export interface AvatarOptions {
  color: number;
  nickname: string;
  layers: LoadedLayer[] | null;
  /**
   * Capas de ítems disponibles, por id. Se construyen todas de una y se muestran
   * según lo equipado: son pocas, y así cambiar de sombrero es instantáneo.
   */
  itemLayers?: Map<string, LoadedLayer>;
  /** El avatar del jugador local se marca: en un grupo, encontrarse cuesta. */
  isSelf: boolean;
}

export function createAvatar({
  color,
  nickname,
  layers,
  itemLayers,
  isSelf,
}: AvatarOptions): AvatarView {
  const root = new Container();

  // La sombra queda fuera de `body` para que no rebote con la caminata. Se agrega
  // primero por orden de dibujo, pero se traza al final: su tamaño depende del
  // ancho real del cuerpo, que un sprite puede no compartir con el vectorial.
  const shadow = new Graphics();
  root.addChild(shadow);

  // Anillo bajo los pies, sólo para el jugador local.
  const ring = new Graphics();
  root.addChild(ring);

  const body = new Container();
  root.addChild(body);

  const built = layers
    ? buildSpriteBody(body, layers, color, itemLayers)
    : buildVectorBody(body, color);

  shadow.ellipse(0, 4, built.halfWidth * 0.8, 6).fill({ color: 0x000000, alpha: 0.18 });

  if (isSelf) {
    ring
      .ellipse(0, 4, built.halfWidth * 1.05, built.halfWidth * 0.5)
      .stroke({ width: 2.5, color: 0xffd75e, alpha: 0.95 });
  }

  /*
   * Sólo el CUERPO responde al dedo.
   *
   * Sin un `hitArea` explícito, Pixi prueba el contorno de cada hijo, y los hijos
   * incluían el nombre y el globo: tocar lo que alguien dijo —hasta 180px de ancho,
   * flotando cien píxeles sobre su cabeza— contaba como tocarlo a él, abría su ficha y
   * el `stopPropagation` del manejador se comía el clic de caminar. En un teléfono, con
   * el mundo escalado y el dedo grueso, eso pasaba casi siempre: el juego parecía no
   * dejarte mover. Declarando el área, `hitPruneFn` descarta el subárbol entero de una.
   *
   * La caja va del suelo a la coronilla, con un respiro abajo para la sombra: es lo que
   * uno señala cuando quiere señalar a un pingüino.
   */
  const half = Math.max(built.halfWidth, 14);
  root.hitArea = new Rectangle(-half, -AVATAR_HEIGHT - 4, half * 2, AVATAR_HEIGHT + 12);

  const label = new Text({
    text: nickname,
    style: {
      fontFamily: "Verdana, Geneva, sans-serif",
      fontSize: 12,
      fontWeight: "bold",
      // El nombre propio en el color de acento, para hallarse de un vistazo.
      fill: isSelf ? 0xffd75e : 0xffffff,
      stroke: { color: 0x1b3f6b, width: 3 },
    },
  });
  label.anchor.set(0.5, 1);
  label.y = -AVATAR_HEIGHT - 8;

  /*
   * El glifo va dentro del `nameplate` y no de `root` por lo mismo que el nombre: la
   * raíz se ordena por los pies, así que cualquier mueble con el borde inferior más
   * abajo lo taparía.
   *
   * Y NO rebota con el cuerpo: es una etiqueta, y una etiqueta que salta con el baile
   * se lee mal.
   */
  const glyph = new Text({
    text: "",
    style: {
      fontFamily: "Verdana, Geneva, sans-serif",
      fontSize: 18,
      fill: 0xffffff,
      stroke: { color: 0x1b3f6b, width: 3 },
    },
  });
  glyph.anchor.set(0.5, 1);
  // Sobre el nombre, con su propio hueco.
  glyph.y = label.y - 14;
  glyph.visible = false;

  const bubble = new Container();
  bubble.visible = false;

  // Fuera de `root`: su capa la decide quien lo monta, y su origen es el de los pies,
  // así que las posiciones de arriba siguen siendo relativas al avatar.
  const nameplate = new Container();
  nameplate.addChild(glyph, label, bubble);

  return {
    root,
    body,
    nameplate,
    label,
    bubble,
    animated: built.animated,
    update: built.update,
    setEquipped: built.setEquipped,
    /*
     * Una vez por CAMBIO, no por fotograma: sólo se tocan el texto y la visibilidad,
     * sin reconstruir el `Text`.
     */
    setGesture: (gesture) => {
      const def = GESTURE_DEFS[gesture];
      glyph.text = def.glyph;
      glyph.visible = def.glyph !== "";
    },
  };
}

interface BuiltBody {
  halfWidth: number;
  animated: boolean;
  update: (facing: Facing, walking: boolean, deltaSeconds: number) => void;
  setEquipped: (itemIds: readonly string[]) => void;
}

/**
 * Resuelve las ocho direcciones a partir de las que existan: directa, si no su
 * espejo, si no `s`, si no cualquiera. Precalculado para no decidir por fotograma.
 */
function resolveClip(available: LoadedClip): ResolvedClip {
  const fallbackFacing = available.has("s") ? "s" : available.keys().next().value;
  const fallback = fallbackFacing ? available.get(fallbackFacing) : undefined;
  const resolved: ResolvedClip = new Map();

  for (const facing of FACINGS) {
    const direct = available.get(facing);
    if (direct) {
      resolved.set(facing, { textures: direct, mirrored: false, source: facing });
      continue;
    }

    const mirror = MIRRORED_FACING[facing];
    const mirrored = mirror ? available.get(mirror) : undefined;
    if (mirror && mirrored) {
      resolved.set(facing, { textures: mirrored, mirrored: true, source: mirror });
      continue;
    }

    if (fallback && fallbackFacing) {
      resolved.set(facing, { textures: fallback, mirrored: false, source: fallbackFacing });
    }
  }

  return resolved;
}

/**
 * Un ítem se dibuja SOBRE el lienzo del cuerpo, así que hereda su espejo.
 *
 * Resolverlo por su cuenta era el bug: un ítem de un solo archivo no tiene espejo que
 * resolver —no hay dirección opuesta que buscar— así que se quedaba sin voltear
 * mientras el cuerpo SÍ se volteaba. Con el avatar autorado en `s se w nw n`, eso
 * dejaba el café en la mano contraria en `e`, `ne` y `sw`: tres de las ocho
 * direcciones, y sólo al girar, que es cuando nadie está mirando los detalles.
 *
 * Que un ítem con arte propio para las cinco coincidiera con el cuerpo era CASUALIDAD
 * —está autorado en el mismo juego de direcciones—, no una garantía. Preguntándole al
 * cuerpo no hay casualidad: la pose que se está dibujando la decide él, y el ítem la
 * acompaña.
 *
 * Por eso el dibujo del ítem se indexa por `source` —la dirección de la que salió la
 * pose del cuerpo— y no por la que mira el jugador. Un ítem que declare arte para una
 * dirección que el cuerpo no tiene es dato muerto, y `check:items` lo caza.
 */
function resolveItemClip(available: LoadedClip, body: ResolvedClip): ResolvedClip {
  const fallbackFacing = available.has("s") ? "s" : available.keys().next().value;
  const fallback = fallbackFacing ? available.get(fallbackFacing) : undefined;
  const resolved: ResolvedClip = new Map();

  for (const facing of FACINGS) {
    const pose = body.get(facing);
    if (!pose) continue;

    const textures = available.get(pose.source) ?? fallback;
    const source = available.has(pose.source) ? pose.source : fallbackFacing;
    if (!textures || !source) continue;

    // El espejo es del CUERPO, siempre. Es lo único que hace que vayan juntos.
    resolved.set(facing, { textures, mirrored: pose.mirrored, source });
  }

  return resolved;
}

function buildSpriteBody(
  body: Container,
  layers: LoadedLayer[],
  color: number,
  itemLayers?: Map<string, LoadedLayer>,
): BuiltBody {
  // La primera capa manda la escala; si cada capa se escalara por su propia
  // altura, los anclajes se desalinearían entre sí. Los ítems la comparten, que es
  // lo que hace que un sombrero caiga en la cabeza y no flotando.
  const first = layers[0]!;
  const reference = (first.idle.get("s") ?? first.idle.values().next().value!)[0]!;
  const scale = AVATAR_HEIGHT / reference.height;

  const built: SpriteLayer[] = [];

  /*
   * El cuerpo ordena sus hijos por `zIndex` en vez de por orden de inserción.
   *
   * Hasta ahora bastaba con añadir los ítems después del cuerpo, porque siempre
   * iban encima. Con la colocación un ítem puede pedir ir DEBAJO —una capucha
   * pasa por detrás de la cabeza— y eso cambia por dirección, así que el orden
   * tiene que poder cambiar en caliente y no sólo al construir.
   */
  body.sortableChildren = true;

  /*
   * La resolución del CUERPO, que es contra la que se alinean los ítems.
   *
   * Se toma de la primera capa —la misma que manda la escala— porque es la que define
   * la pose. Si el cuerpo tuviera varias capas con direcciones distintas ya estarían
   * desalineadas entre sí, que es un problema anterior a éste.
   */
  const bodyIdle = resolveClip(first.idle);

  const addLayer = (layer: LoadedLayer, itemId: string | null): void => {
    const idle = itemId === null ? resolveClip(layer.idle) : resolveItemClip(layer.idle, bodyIdle);
    const walk = layer.walk
      ? itemId === null
        ? resolveClip(layer.walk)
        : resolveItemClip(layer.walk, bodyIdle)
      : null;

    const sprite = new Sprite(idle.get("s")?.textures[0]);
    sprite.anchor.set(0.5, 1);
    sprite.scale.set(scale);
    if (layer.tinted) sprite.tint = color;
    // Los ítems arrancan ocultos: se encienden cuando llega el estado.
    sprite.visible = itemId === null;

    body.addChild(sprite);
    built.push({ sprite, idle, walk, scale, itemId, placement: layer.placement });
  };

  for (const layer of layers) addLayer(layer, null);
  // Después del cuerpo, así los ítems se dibujan encima.
  for (const [itemId, layer] of itemLayers ?? []) addLayer(layer, itemId);

  // Sólo cuenta como animado si hay más de un fotograma que reproducir.
  const animated = built.some((layer) =>
    layer.walk ? [...layer.walk.values()].some((entry) => entry.textures.length > 1) : false,
  );

  /**
   * Cuántos fotogramas tiene el ciclo más largo, para saber dónde cierra.
   *
   * Se toma el máximo y no el de cada capa: las capas comparten reloj, así que si
   * cada una cerrara por su cuenta la parada dejaría al sombrero en un fotograma y
   * al cuerpo en otro.
   */
  const walkFrames = Math.max(
    1,
    ...built.flatMap((layer) =>
      layer.walk ? [...layer.walk.values()].map((entry) => entry.textures.length) : [1],
    ),
  );

  let clock = 0;
  let wasWalking = false;
  /**
   * Hasta qué fotograma se sigue caminando después de parar.
   *
   * Al llegar, el ciclo se cortaba donde tocara: una zancada a medias congelada de
   * golpe, que es de esas cosas que se ven mal sin que sepas por qué. Se deja correr
   * hasta cerrar el ciclo —como mucho una vuelta— y ahí sí se pasa a quieto, que es
   * lo que hacía Club Penguin: el balanceo vuelve a la vertical antes de parar.
   */
  let settleUntil = 0;

  const update = (facing: Facing, walking: boolean, deltaSeconds: number): void => {
    const stepNow = Math.floor(clock * WALK_FPS);

    if (walking !== wasWalking) {
      wasWalking = walking;

      if (walking) {
        // Reiniciar al arrancar evita entrar al ciclo por la mitad.
        clock = 0;
        settleUntil = 0;
      } else {
        // Lo que falta para volver al fotograma neutro, sin reiniciar el reloj.
        settleUntil = stepNow + ((walkFrames - (stepNow % walkFrames)) % walkFrames);
      }
    }

    clock += deltaSeconds;

    const step = Math.floor(clock * WALK_FPS);
    const settling = !walking && step < settleUntil;

    for (const layer of built) {
      // Una capa oculta no necesita textura nueva; saltarla ahorra trabajo por
      // fotograma y por jugador, que es donde esto se multiplica.
      if (!layer.sprite.visible) continue;

      const clip = ((walking || settling) && layer.walk) || layer.idle;
      const entry = clip.get(facing);
      if (!entry || entry.textures.length === 0) continue;

      const index = entry.textures.length === 1 ? 0 : step % entry.textures.length;

      layer.sprite.texture = entry.textures[index]!;

      /*
       * La colocación se lee de `facing` —la perspectiva que se está viendo— y no de
       * la dirección de la que salió el dibujo. Es lo que deja ajustar una gorra en las
       * cinco perspectivas aunque esté dibujada una sola vez. El espejo lo resuelve
       * `placementFor`, contra el cuerpo.
       */
      const place = placementFor(layer.placement, facing);
      const scaled = layer.scale * place.scale;

      // El espejo es un signo en la escala, no una textura aparte.
      layer.sprite.scale.x = entry.mirrored ? -scaled : scaled;
      layer.sprite.scale.y = scaled;

      /*
       * El desplazamiento va en unidades del lienzo, así que se multiplica por la
       * escala COMPARTIDA y no por la del ítem: agrandar un sombrero no debe moverlo.
       */
      layer.sprite.position.set(place.x * layer.scale, place.y * layer.scale);
      if (layer.sprite.zIndex !== place.z) layer.sprite.zIndex = place.z;
    }
  };

  const setEquipped = (itemIds: readonly string[]): void => {
    const worn = new Set(itemIds);
    for (const layer of built) {
      if (layer.itemId === null) continue;
      layer.sprite.visible = worn.has(layer.itemId);
    }
  };

  return { halfWidth: (reference.width * scale) / 2, animated, update, setEquipped };
}

/** Personaje vectorial: el fallback mientras no hay arte. */
function buildVectorBody(body: Container, color: number): BuiltBody {
  const torso = new Graphics();
  torso.ellipse(0, -BODY_RY, BODY_RX, BODY_RY).fill(color);
  torso.ellipse(0, -BODY_RY + 4, BODY_RX * 0.62, BODY_RY * 0.68).fill(0xf5f2e8);
  body.addChild(torso);

  const face = new Graphics();
  face.circle(-6, -BODY_RY - 6, 4).fill(0xffffff);
  face.circle(6, -BODY_RY - 6, 4).fill(0xffffff);
  face.circle(-5, -BODY_RY - 6, 2).fill(0x1a1a1a);
  face.circle(7, -BODY_RY - 6, 2).fill(0x1a1a1a);
  face.poly([-5, -BODY_RY + 2, 5, -BODY_RY + 2, 0, -BODY_RY + 8]).fill(0xf29104);
  body.addChild(face);

  const feet = new Graphics();
  feet.ellipse(-7, 0, 6, 3).fill(0xf29104);
  feet.ellipse(7, 0, 6, 3).fill(0xf29104);
  body.addChild(feet);

  // El personaje vectorial no lleva ítems: es el fallback, no el juego.
  return { halfWidth: BODY_RX, animated: false, update: () => {}, setEquipped: () => {} };
}

/** Reemplaza el contenido del globo de diálogo. */
export function setBubble(view: AvatarView, text: string): void {
  view.bubble.removeChildren();

  const content = new Text({
    text,
    style: {
      fontFamily: "Verdana, Geneva, sans-serif",
      fontSize: 12,
      fill: 0x1a1a1a,
      wordWrap: true,
      wordWrapWidth: 160,
      align: "center",
    },
  });
  content.anchor.set(0.5, 0.5);

  const padX = 10;
  const padY = 7;
  const w = content.width + padX * 2;
  const h = content.height + padY * 2;

  const backdrop = new Graphics();
  backdrop
    .roundRect(-w / 2, -h / 2, w, h, 9)
    .fill(0xffffff)
    .stroke({ width: 2, color: 0x8fa9c4 });
  backdrop.poly([-6, h / 2 - 1, 6, h / 2 - 1, 0, h / 2 + 7]).fill(0xffffff);

  view.bubble.addChild(backdrop, content);
  view.bubble.y = -AVATAR_HEIGHT - 26 - h / 2;
  view.bubble.visible = true;
}
